import { useMemo } from "react";
import { CellProps, Column } from "react-table";

import {
  MaterialisationStatus,
  MaterializationType,
  ModelListItemFragment,
  OrchestrationSchedulerType,
} from "@/apollo/types";
import { Badge } from "@/components/elements/Badge";
import { Button } from "@/components/elements/Button";
import { CronDisplayReadable } from "@/components/elements/CronDisplayReadable";
import DefaultTable from "@/components/elements/DefaultTable";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/elements/HoverCard";
import { LoadingFull } from "@/components/elements/LoadingComponents";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
} from "@/components/elements/Modal";
import Tooltip from "@/components/elements/Tooltip";
import MainPageLayout from "@/components/layouts/MainPageLayout";
import { formatModelName } from "@/features/models";
import { useDisclosure } from "@/hooks/useDisclosure";
import { ModelViewInfo } from "@/pages/ModelTool/ModelView";
import { useListModels } from "@/pages/ModelTool/hooks/useListModels";
import { LinkWithSlug } from "@/routes";
import { formatDate } from "@/shared/formatters";
import { truncateStringMiddle } from "@/shared/formatters/truncation";
import { ComponentProps } from "@tw-classed/react";

import { ModelIcon } from "../ModelTool/components/ModelIcon/ModelIcon";

type Row = ModelListItemFragment;

const columns: Column<Row>[] = [
  {
    Header: "Model",
    Cell: ({ row }: CellProps<ModelListItemFragment>) => {
      const model = row.original;
      const modelPath = formatModelName(model);
      const truncatedModelPath = truncateStringMiddle(modelPath, 30);
      return (
        <div className="-mx-1 whitespace-nowrap">
          <Tooltip content={modelPath ?? null}>
            <Button
              variant="ghost"
              icon={<ModelIcon model={model} />}
              size="sm"
              className="h-7 min-w-0 max-w-52 truncate px-1.5 text-xs font-normal leading-none hover:border-gray-300 dark:hover:border-gray-600 sm:text-sm"
              as={LinkWithSlug}
              to={`/editor/${model.id}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <span className="truncate">{truncatedModelPath}</span>
            </Button>
          </Tooltip>
        </div>
      );
    },
  },
  {
    Header: "Schedule",
    Cell: ({ row }: CellProps<Row>) => (
      <div className="whitespace-nowrap text-sm">
        {row.original.localMaterializationJob?.cronExpression ? (
          <CronDisplayReadable
            capitalize
            cron={row.original.localMaterializationJob?.cronExpression}
          />
        ) : (
          "Unknown schedule"
        )}
      </div>
    ),
  },
  {
    Header: "Next run",
    Cell: ({ row }: CellProps<Row>) => {
      const job = row.original.localMaterializationJob;
      if (job?.nextRun == null) {
        return <div className="text-muted-foreground">-</div>;
      }
      return (
        <Tooltip
          content={formatDate(job.nextRun, {
            timeStyle: "short",
            dateStyle: "short",
          })}
        >
          <div className="whitespace-nowrap text-muted-foreground">
            <FormatRelativeTime date={job.nextRun} />
          </div>
        </Tooltip>
      );
    },
  },
  {
    Header: "Last run",
    Cell: ({ row }: CellProps<Row>) => {
      const latestSyncTime = row.original.dwTable?.latestSyncTime;
      if (latestSyncTime == null) {
        return <div className="text-muted-foreground">-</div>;
      }
      return (
        <Tooltip
          content={formatDate(latestSyncTime, {
            timeStyle: "short",
            dateStyle: "short",
          })}
        >
          <div className="whitespace-nowrap text-muted-foreground">
            <FormatRelativeTime date={latestSyncTime} />
          </div>
        </Tooltip>
      );
    },
  },
  {
    Header: "Status",
    Cell: ({ row }: CellProps<Row>) => {
      const variantMap: Record<
        MaterialisationStatus,
        ComponentProps<typeof Badge>["variant"]
      > = {
        [MaterialisationStatus.Failed]: "danger",
        [MaterialisationStatus.Running]: "info",
        [MaterialisationStatus.Succeeded]: "success",
      };
      if (row.original.dwTable?.status == null) {
        return <div className="text-muted-foreground">None</div>;
      }
      const variant = variantMap[row.original.dwTable.status];
      return <Badge variant={variant}>{row.original.dwTable.status}</Badge>;
    },
  },
  {
    Header: "Last error",
    Cell: ({ row }: CellProps<Row>) => {
      const errorMessage = row.original.dwTable?.latestRunErrorMessage;
      if (errorMessage == null) {
        return <div className="text-muted-foreground">-</div>;
      }
      return (
        <div className="min-w-0 max-w-52 truncate text-muted-foreground">
          <HoverCard openDelay={100}>
            <HoverCardTrigger asChild>
              <span>{errorMessage}</span>
            </HoverCardTrigger>
            <HoverCardContent className="w-full max-w-md break-all">
              <span>{errorMessage}</span>
            </HoverCardContent>
          </HoverCard>
        </div>
      );
    },
  },
  {
    Header: "Tests",
    Cell: ({ row }: CellProps<Row>) => {
      return <div className="text-muted-foreground">Skipped</div>;
    },
  },
];

const LocalJobs = () => {
  const { models, loadingModels } = useLocalJobsModels();
  const { isOpen, onOpen, onClose, data: modelId } = useDisclosure<string>();
  return (
    <>
      <MainPageLayout>
        <div>
          {loadingModels && <LoadingFull />}
          <DefaultTable
            onRowClick={(row) => onOpen(row.id)}
            columns={columns}
            data={models}
            empty={
              <div className="w-full space-y-4 text-center dark:text-white">
                <p className="text-lg">No local jobs</p>
                <p className="flex flex-col text-sm">
                  <span>
                    Models materialized as tables on a local schedule will
                    appear here.
                  </span>
                </p>
              </div>
            }
          />
        </div>
      </MainPageLayout>
      {modelId && (
        <Modal size="lg" isOpen={isOpen} onClose={onClose}>
          <ModalCloseButton />
          <ModalBody>
            <div style={{ height: "70vh" }}>
              <ModelViewInfo modelId={modelId} />
            </div>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default LocalJobs;

const useLocalJobsModels = () => {
  const { models, loadingModels } = useListModels();
  return useMemo(() => {
    return {
      models: models.filter(
        (m) =>
          !!m.publishedQuery &&
          m.materializationType === MaterializationType.Table &&
          m.orchestrationScheduler === OrchestrationSchedulerType.Local,
      ),
      loadingModels,
    };
  }, [models, loadingModels]);
};
